const roundToNearestFloor = (num) => Math.floor(num);
const roundToNearestRound = (num) => Math.round(num);

export const calculateFixedScenario = (capital, scenarioRate, assumptions) => {
  const { b, c, d } = assumptions;
  const gainOrLoss = roundToNearestFloor(capital * scenarioRate);
  const grossValue = roundToNearestFloor(capital + gainOrLoss);
  const averageAUM = roundToNearestFloor((capital + grossValue) / 2);
  const otherExpense = roundToNearestFloor(averageAUM * -(c / 100));
  const brokerageCost = roundToNearestFloor(averageAUM * -(d / 100));
  const managementFees = roundToNearestFloor((averageAUM + otherExpense + brokerageCost) * -(b / 100));
  const gstOnManagementFee = roundToNearestFloor(managementFees * 0.18);
  const totalCharges = roundToNearestFloor(otherExpense + brokerageCost + managementFees + gstOnManagementFee);
  const netValue = roundToNearestFloor(grossValue + totalCharges);
  const portfolioReturn = ((netValue - capital) / capital) * 100;

  return {
    gainOrLoss,
    grossValue,
    averageAUM,
    otherExpense,
    brokerageCost,
    managementFees,
    gstOnManagementFee,
    totalCharges,
    netValue,
    portfolioReturn,
  };
};

export const calculateMultiYearFixedScenario = (initialCapital, scenarioRates, assumptions, feeDetails) => {
  const { b, c, d } = assumptions;
  const { performanceFee, hurdleRate } = feeDetails;

  let highWaterMark = initialCapital;
  let capital = initialCapital;

  return scenarioRates.map((scenarioRate, year) => {
    const startingCapital = capital;
    const gainOrLoss = roundToNearestRound(capital * scenarioRate);
    const grossValue = roundToNearestRound(capital + gainOrLoss);
    const averageAUM = roundToNearestRound((capital + grossValue) / 2);
    const otherExpense = roundToNearestRound(averageAUM * -(c / 100));
    const brokerageCost = roundToNearestRound(averageAUM * -(d / 100));
    const managementFees = roundToNearestRound((averageAUM + otherExpense + brokerageCost) * -(b / 100));
    const gstOnManagementFee = roundToNearestRound(managementFees * 0.18);
    const totalCharges = roundToNearestRound(otherExpense + brokerageCost + managementFees + gstOnManagementFee);
    const grossValueBeforePerformanceFee = roundToNearestRound(grossValue + totalCharges);
    const hurdleRateValue = parseFloat(hurdleRate.replace('%', '')) / 100;
    const HurdleValue = roundToNearestRound(highWaterMark * (1 + hurdleRateValue));
    const isPerformanceFeeApplicable = grossValueBeforePerformanceFee > HurdleValue;
    const portfolioReturnSubjectToPerformanceFee = roundToNearestRound(isPerformanceFeeApplicable ? grossValueBeforePerformanceFee - HurdleValue : 0);
    const performanceFeeAmount = roundToNearestRound(portfolioReturnSubjectToPerformanceFee * (performanceFee / 100));
    const gstOnPerformanceFee = roundToNearestRound(performanceFeeAmount * 0.18);
    const netValueAfterFees = roundToNearestRound(grossValueBeforePerformanceFee - performanceFeeAmount - gstOnPerformanceFee);
    const portfolioReturn = ((netValueAfterFees - startingCapital) / startingCapital) * 100;
    highWaterMark = Math.max(HurdleValue, netValueAfterFees);
    const highWaterMarkInvestor = Math.max(HurdleValue, grossValueBeforePerformanceFee);
    capital = netValueAfterFees;

    return {
      year: year + 1,
      gainOrLoss,
      grossValue,
      averageAUM,
      otherExpense,
      brokerageCost,
      managementFees,
      gstOnManagementFee,
      totalCharges,
      grossValueBeforePerformanceFee,
      HurdleValue,
      portfolioReturnSubjectToPerformanceFee,
      performanceFeeAmount,
      gstOnPerformanceFee,
      netValueAfterFees,
      portfolioReturn,
      highWaterMark,
      highWaterMarkInvestor,
      capital: startingCapital,
    };
  });
};

export const calculateHybridScenario = (capital, scenarioRate, assumptions, feeDetails, previousHighWaterMark) => {
  const { b, c, d } = assumptions;
  const { performanceFee, hurdleRate } = feeDetails;
  const gainOrLoss = roundToNearestFloor(capital * scenarioRate);
  const grossValue = roundToNearestFloor(capital + gainOrLoss);
  const averageAUM = roundToNearestFloor((capital + grossValue) / 2);
  const otherExpense = roundToNearestFloor(averageAUM * -(c / 100));
  const brokerageCost = roundToNearestFloor(averageAUM * -(d / 100));
  const managementFees = roundToNearestFloor((averageAUM + otherExpense + brokerageCost) * -(b / 100));
  const gstOnManagementFee = roundToNearestFloor(managementFees * 0.18);
  const totalCharges = roundToNearestFloor(otherExpense + brokerageCost + managementFees + gstOnManagementFee);
  const grossValueBeforePerformanceFee = roundToNearestFloor(grossValue + totalCharges);
  const hurdleRateValue = parseFloat(hurdleRate.replace('%', '')) / 100;
  const HurdleValue = roundToNearestFloor(previousHighWaterMark ? previousHighWaterMark * (1 + hurdleRateValue) : capital * (1 + hurdleRateValue));
  const isPerformanceFeeApplicable = grossValueBeforePerformanceFee > HurdleValue;
  const portfolioReturnSubjectToPerformanceFee = roundToNearestFloor(isPerformanceFeeApplicable ? (grossValueBeforePerformanceFee - HurdleValue) : 0);
  const performanceFeeAmount = roundToNearestFloor(portfolioReturnSubjectToPerformanceFee * -(performanceFee / 100));
  const gstOnPerformanceFee = roundToNearestFloor(performanceFeeAmount * 0.18);
  const netValueAfterFees = roundToNearestFloor(grossValueBeforePerformanceFee + performanceFeeAmount + gstOnPerformanceFee);
  const portfolioReturn = ((netValueAfterFees - capital) / capital) * 100;
  const highWaterMarkFromPortfolio = Math.max(HurdleValue, netValueAfterFees);
  const highWaterMarkFromInvestor = Math.max(grossValueBeforePerformanceFee, HurdleValue);

  return {
    gainOrLoss,
    grossValue,
    averageAUM,
    otherExpense,
    brokerageCost,
    managementFees,
    gstOnManagementFee,
    totalCharges,
    grossValueBeforePerformanceFee,
    HurdleValue,
    portfolioReturnSubjectToPerformanceFee,
    performanceFeeAmount,
    gstOnPerformanceFee,
    netValueAfterFees,
    portfolioReturn,
    highWaterMarkFromPortfolio,
    highWaterMarkFromInvestor,
  };
};

export const calculateMultiYearHybridScenario = (initialCapital, scenarioRates, assumptions, feeDetails) => {
  const { b, c, d } = assumptions;
  const { performanceFee, hurdleRate } = feeDetails;

  let highWaterMark = initialCapital;
  let capital = initialCapital;

  return scenarioRates.map((scenarioRate, year) => {
    const startingCapital = capital;
    const gainOrLoss = roundToNearestRound(capital * scenarioRate);
    const grossValue = roundToNearestRound(capital + gainOrLoss);
    const averageAUM = roundToNearestRound((capital + grossValue) / 2);
    const otherExpense = roundToNearestRound(averageAUM * -(c / 100));
    const brokerageCost = roundToNearestRound(averageAUM * -(d / 100));
    const managementFees = roundToNearestRound((averageAUM + otherExpense + brokerageCost) * -(b / 100));
    const gstOnManagementFee = roundToNearestRound(managementFees * 0.18);
    const totalCharges = roundToNearestRound(otherExpense + brokerageCost + managementFees + gstOnManagementFee);
    const grossValueBeforePerformanceFee = roundToNearestRound(grossValue + totalCharges);
    const hurdleRateValue = parseFloat(hurdleRate.replace('%', '')) / 100;
    const HurdleValue = roundToNearestRound(highWaterMark * (1 + hurdleRateValue));
    const isPerformanceFeeApplicable = grossValueBeforePerformanceFee > HurdleValue;
    const portfolioReturnSubjectToPerformanceFee = roundToNearestRound(isPerformanceFeeApplicable ? grossValueBeforePerformanceFee - HurdleValue : 0);
    const performanceFeeAmount = roundToNearestRound(portfolioReturnSubjectToPerformanceFee * (performanceFee / 100));
    const gstOnPerformanceFee = roundToNearestRound(performanceFeeAmount * 0.18);
    const netValueAfterFees = roundToNearestRound(grossValueBeforePerformanceFee - performanceFeeAmount - gstOnPerformanceFee);
    const portfolioReturn = ((netValueAfterFees - startingCapital) / startingCapital) * 100;
    highWaterMark = Math.max(HurdleValue, netValueAfterFees);
    const highWaterMarkInvestor = Math.max(HurdleValue, grossValueBeforePerformanceFee);
    capital = netValueAfterFees;

    return {
      year: year + 1,
      gainOrLoss,
      grossValue,
      averageAUM,
      otherExpense,
      brokerageCost,
      managementFees,
      gstOnManagementFee,
      totalCharges,
      grossValueBeforePerformanceFee,
      HurdleValue,
      portfolioReturnSubjectToPerformanceFee,
      performanceFeeAmount,
      gstOnPerformanceFee,
      netValueAfterFees,
      portfolioReturn,
      highWaterMark,
      highWaterMarkInvestor,
      capital: startingCapital,
    };
  });
};

export const calculateMultiYearHybridPortfolioScenario = (initialCapital, scenarioRates, assumptions, feeDetails) => {
  const { b, c, d } = assumptions;
  const { performanceFee, managementFee } = feeDetails;

  let capital = initialCapital;
  let previousYearLoss = 0;

  return scenarioRates.map((gainOrLoss, year) => {
      const i = year === 0 ? initialCapital : capital;
      const ii = roundToNearestRound(gainOrLoss);
      const iii = roundToNearestRound(i + ii);
      const iv = roundToNearestRound((i + iii) / 2);
      const v = roundToNearestRound(iv * -(c / 100));
      const vi = roundToNearestRound(iv * -(d / 100));
      const vii = roundToNearestRound((iv + v + vi) * -(b / 100));
      const viii = roundToNearestRound(vii * 0.18);
      const ix = roundToNearestRound(v + vi + vii + viii);
      const x = roundToNearestRound(iii + ix);
      const xi = ii + vii > 0 ? "Yes" : "No";
      const xii = ii + vii > 0 ? roundToNearestRound(ii + vii) : 0;
      const xiii = previousYearLoss < 0 ? previousYearLoss : 0;
      const xiv = roundToNearestRound(xii + xiii);
      const xv = roundToNearestRound(xiv * -(performanceFee / 100));
      const xvi = roundToNearestRound(xv * 0.18);
      const xvii = roundToNearestRound(x + xv + xvi);
      const xviii = ii < 0 ? ii : 0;
      const portfolioReturn = ((xvii - i) / i) * 100;
      const HurdleValue = 0;
      const highWaterMark = xvii;
      const highWaterMarkInvestor = x;

      capital = xvii;
      previousYearLoss = xviii;
      return {
          capital: i,
          gainOrLoss: ii,
          grossValue: iii,
          averageAUM: iv,
          otherExpense: v,
          brokerageCost: vi,
          managementFees: vii,
          gstOnManagementFee: viii,
          totalCharges: ix,
          grossValueBeforePerformanceFee: x,
          HurdleValue,
          performanceFeeApplicability: xi,
          portfolioReturnSubjectToPerformanceFee: xii,
          performanceFeeAmount: xv,
          gstOnPerformanceFee: xvi,
          netValueAfterFees: xvii,
          portfolioReturn,
          highWaterMark,
          highWaterMarkInvestor,
          i, ii, iii, iv, v, vi, vii, viii, ix, x, xi, xii, xiii, xiv, xv, xvi, xvii, xviii,
      };
  });
};

export const calculateHybridPortfolioScenario = (initialCapital, scenarioRates, assumptions, feeDetails) => {
  const { b, c, d } = assumptions;
  const { performanceFee, managementFee } = feeDetails;

  let capital = initialCapital;
  let previousYearLoss = 0;

  return scenarioRates.map((gainOrLoss, year) => {
      const i = initialCapital;
      const ii = roundToNearestRound(gainOrLoss);
      const iii = roundToNearestRound(i + ii);
      const iv = roundToNearestRound((i + iii) / 2);
      const v = roundToNearestRound(iv * -(c / 100));
      const vi = roundToNearestRound(iv * -(d / 100));
      const vii = roundToNearestRound((iv + v + vi) * -(b / 100));
      const viii = roundToNearestRound(vii * 0.18);
      const ix = roundToNearestRound(v + vi + vii + viii);
      const x = roundToNearestRound(iii + ix);
      const xi = ii + vii > 0 ? "Yes" : "No";
      const xii = ii + vii > 0 ? roundToNearestRound(ii + vii) : 0;
      const xiii = roundToNearestRound(xii * -(performanceFee / 100));
      const xiv = roundToNearestRound(xiii * 0.18);
      const xv = roundToNearestRound(x + xiii + xiv);
      const portfolioReturn = ((xv - i) / i) * 100;
      const HurdleValue = 0;
      const highWaterMarkFromPortfolio = xv;
      const highWaterMarkFromInvestor = x;
      return {
          capital: i,
          gainOrLoss: ii,
          grossValue: iii,
          averageAUM: iv,
          otherExpense: v,
          brokerageCost: vi,
          managementFees: vii,
          gstOnManagementFee: viii,
          totalCharges: ix,
          grossValueBeforePerformanceFee: x,
          HurdleValue,
          performanceFeeApplicability: xi,
          portfolioReturnSubjectToPerformanceFee: xii,
          performanceFeeAmount: xiii,
          gstOnPerformanceFee: xiv,
          netValueAfterFees: xv,
          portfolioReturn,
          highWaterMarkFromPortfolio,
          highWaterMarkFromInvestor,
          i, ii, iii, iv, v, vi, vii, viii, ix, x, xi, xii, xiii, xiv, xv,
      };
  });
};
